import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetPortfolioBrandsV1PortfoliosPortfolioIdBrandsGetData,
  type GetPortfolioBrandsV1PortfoliosPortfolioIdBrandsGetError,
  type GetPortfolioBrandsV1PortfoliosPortfolioIdBrandsGetResponse,
  getPortfolioBrandsV1PortfoliosPortfolioIdBrandsGet,
} from '../../generated';

export type GetPortfolioBrandsQueryVariables = GetPortfolioBrandsV1PortfoliosPortfolioIdBrandsGetData['path'] &
  GetPortfolioBrandsV1PortfoliosPortfolioIdBrandsGetData['query'];

export type GetPortfolioBrandsQuery = {
  data: GetPortfolioBrandsV1PortfoliosPortfolioIdBrandsGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useGetPortfolioBrandsQuery = <
  TData = GetPortfolioBrandsQuery,
  TError = GetPortfolioBrandsV1PortfoliosPortfolioIdBrandsGetError,
>(
  { portfolio_id, limit, offset, search }: GetPortfolioBrandsQueryVariables,
  options?: Omit<UseQueryOptions<GetPortfolioBrandsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetPortfolioBrandsQuery, TError, TData>({
    queryKey: ['GetPortfolioBrands', { portfolio_id, limit, offset, search }],
    queryFn: async () => {
      const { data, response } = await getPortfolioBrandsV1PortfoliosPortfolioIdBrandsGet({
        path: { portfolio_id },
        query: { limit, offset, search },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useGetPortfolioBrandsQuery.getKey = ({ portfolio_id, limit, offset, search }: GetPortfolioBrandsQueryVariables) => [
  'GetPortfolioBrands',
  { portfolio_id, limit, offset, search },
];

useGetPortfolioBrandsQuery.fetcher = (
  { portfolio_id, limit, offset, search }: GetPortfolioBrandsQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  // Returns a function for backward compatibility with the old fetcher pattern
  return async () => {
    const { data, response } = await getPortfolioBrandsV1PortfoliosPortfolioIdBrandsGet({
      path: { portfolio_id },
      query: { limit, offset, search },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
